import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Container, Row, Col } from 'react-bootstrap'; // Assuming react-bootstrap is used

const LoginPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [bluekeyLoading, setBluekeyLoading] = useState(false);
    const navigate = useNavigate();

    // Bluekey SSO configuration
    const BLUEKEY_SOFTWARE_ID = '1d59453c-c2fc-4de3-9f46-c85ee8e3cf4e';
    const BLUEKEY_PORTAL_URL = 'https://iis.memphis.edu/static/bluekey/';
    const BLUEKEY_ORIGIN = 'https://iis.memphis.edu';
    const BLUEKEY_TOKEN_STORAGE_KEY = 'bluekey_token';

    // Handle Bluekey postMessage authentication
    const handleBluekeyMessage = useCallback((event) => {
        // CRITICAL: Verify the origin
        if (event.origin !== BLUEKEY_ORIGIN) return;

        // Parse data if it arrives as a string (compatibility)
        let data = event.data;
        if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch (e) { return; }
        }

        // Check for the success event type
        if (data?.type === 'bluekey-login-success') {
            const { accessToken, email } = data;

            // Store the token for future API requests
            localStorage.setItem(BLUEKEY_TOKEN_STORAGE_KEY, accessToken);

            // Redirect to Overleaf with the Bluekey token as a query parameter and Authorization header
            setBluekeyLoading(false);
            setError('');
            
            // Close the popup if still open
            if (window.bluekeyPopup) {
                window.bluekeyPopup.close();
            }

            // Redirect — the proxy will pick up the token and auto-provision the user
            window.location.href = `/?bluekey_token=${encodeURIComponent(accessToken)}`;
        }
    }, []);

    // Register Bluekey message listener on mount
    useEffect(() => {
        window.addEventListener('message', handleBluekeyMessage);
        return () => window.removeEventListener('message', handleBluekeyMessage);
    }, [handleBluekeyMessage]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Login as a regular user
            const response = await axios.post(apiPrefix + '/api/user-login', {
                email: email.toLowerCase(),
                password,
            });

            if (response.data.success) {
                localStorage.setItem('jwt_token', response.data.token);
                localStorage.setItem('user', JSON.stringify({ userId: response.data.userId, email: response.data.email, isAdmin: response.data.isAdmin }));

                // Redirect based on user role or if password reset is required
                if (response.data.isAdmin) {
                    navigate('/portal-admin');
                } else if (response.data.requiresPasswordReset) {
                    // Redirect to password reset page with user info
                    navigate('/temp-password', { state: { userId: response.data.userId, email: response.data.email } });
                } else {
                    // Default redirect for regular users, potentially to their assigned instance or dashboard
                    // For now, redirecting to download page as per previous context.
                    navigate('/download');
                }
            } else {
                setError(response.data.error || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            console.error('Login error:', err);
            const errorMessage = err.response?.data?.error || err.message || 'An unexpected error occurred during login.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBluekeyLogin = () => {
        setBluekeyLoading(true);
        setError('');
        
        const url = `${BLUEKEY_PORTAL_URL}?appUuid=${BLUEKEY_SOFTWARE_ID}&mode=popup`;
        
        // Open a centered popup window
        window.bluekeyPopup = window.open(
            url,
            'bluekeyLogin',
            'width=800,height=900'
        );
        
        // If popup was blocked, show an error
        if (!window.bluekeyPopup || window.bluekeyPopup.closed) {
            setError('Popup was blocked. Please allow popups for this site and try again.');
            setBluekeyLoading(false);
        }
    };

    // Note: Admin login is handled by a separate mechanism or form, typically at '/portal-admin'.
    // This LoginPage is primarily for regular user authentication.

    return (
        <Container>
            <Row className="justify-content-md-center mt-5">
                <Col md="6">
                    <h2 className="text-center mb-4">Welcome to Overleaf Proxy</h2>
                    
                    {/* Bluekey SSO Button */}
                    <div className="d-grid gap-2 mb-4">
                        <Button
                            variant="success"
                            size="lg"
                            onClick={handleBluekeyLogin}
                            disabled={bluekeyLoading}
                        >
                            {bluekeyLoading ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{' '}
                                    Opening Bluekey...
                                </>
                            ) : (
                                'Sign in with Bluekey'
                            )}
                        </Button>
                        <div className="text-center text-muted small">
                            Use your University of Memphis single sign-on
                        </div>
                    </div>

                    <hr className="my-4" />
                    <h5 className="text-center text-muted mb-3">Or sign in with email and password</h5>

                    <Form onSubmit={handleLogin}>
                        <Form.Group className="mb-3" controlId="formBasicEmail">
                            <Form.Label>Email address</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formBasicPassword">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </Form.Group>

                        {error && <Alert variant="danger">{error}</Alert>}

                        <div className="d-grid gap-2">
                            <Button variant="primary" type="submit" disabled={loading}>
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />{' '}
                                        Logging in...
                                    </>
                                ) : (
                                    'Login'
                                )}
                            </Button>
                            {/* Admin login button - Only use if this form is for both user types */}
                            {/* <Button variant="secondary" onClick={handleAdminLogin} disabled={loading} className="mt-2">
                                {loading ? 'Logging in as Admin...' : 'Login as Admin'}
                            </Button> */}
                        </div>
                    </Form>
                    <div className="text-center mt-3">
                        <p>Don't have an account? <a href="../access-request">Request Access</a></p>
                        {/* Link to password reset if applicable */}
                        {/* <p><a href="/forgot-password">Forgot Password?</a></p> */}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default LoginPage;
